import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { getErrorMessage } from '../api/errorMessage';
import { tr } from '../i18n';
import { Player, PlayersPage, Tag } from '../types';
import PlayerAvatar from '../ui/avatar/PlayerAvatar';
import { EmptyState, ErrorState } from '../ui/system-states';
import { TableSkeleton } from '../ui/TableSkeleton';
import { useAppToast } from '../ui/toast';
import './PlayersPage.css';

// ─── helpers ─────────────────────────────────────────────────────────────────

// Доступные режимы матчей — рейтинг показывается по выбранному режиму.
const MATCH_MODES: { code: string; label: string }[] = [
  { code: 'PVP_SOLO', label: 'Solo' },
  { code: 'PVP_DUO', label: 'Duo' }
];

// Рейтинг/матчи строго по выбранному режиму — складывать по типам нельзя.
const modeRating = (player: Player, mode: string): number => player.ratings?.[mode] ?? 0;
const modeMatches = (player: Player, mode: string): number => player.matchesCounts?.[mode] ?? 0;

const fmtRating = (n: number): string => {
  const s = Math.round(n).toString();
  if (s.length <= 3) return s;
  return s.slice(0, s.length - 3) + ' ' + s.slice(s.length - 3);
};

// ─── types ────────────────────────────────────────────────────────────────────

type SortField = 'name' | 'rating' | 'matchesCount' | 'balance';
type SortDir = 'asc' | 'desc';

type FiltersState = {
  search: string;
  banned: '' | 'false' | 'true';
  everBanned: '' | 'false' | 'true';
  matchType: string;
};

type PlayersPageProps = {
  selfUid?: string | null;
  viewAsAdmin?: boolean;
};

const SEARCH_DEBOUNCE_MS = 300;

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'rating:desc', label: tr.players.list.sort.ratingDesc },
  { value: 'rating:asc', label: tr.players.list.sort.ratingAsc },
  { value: 'matchesCount:desc', label: tr.players.list.sort.matchesDesc },
  { value: 'matchesCount:asc', label: tr.players.list.sort.matchesAsc },
  { value: 'balance:desc', label: tr.players.list.sort.balanceDesc },
  { value: 'balance:asc', label: tr.players.list.sort.balanceAsc },
  { value: 'name:asc', label: tr.players.list.sort.nameAsc },
  { value: 'name:desc', label: tr.players.list.sort.nameDesc }
];

const DEFAULT_SORT = 'rating:desc';

const defaultFilters: FiltersState = { search: '', banned: '', everBanned: '', matchType: 'PVP_SOLO' };

const pickOption = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const readFiltersFromUrl = (params: URLSearchParams, viewAsAdmin: boolean): FiltersState => ({
  search: params.get('q') ?? '',
  banned: pickOption(params.get('banned'), ['', 'true', 'false'] as const, ''),
  everBanned: viewAsAdmin ? pickOption(params.get('everBanned'), ['', 'true', 'false'] as const, '') : '',
  matchType: pickOption(params.get('mode'), MATCH_MODES.map(m => m.code), defaultFilters.matchType)
});

const readSortFromUrl = (params: URLSearchParams): { field: SortField; dir: SortDir } => {
  const value = pickOption(params.get('sort'), SORT_OPTIONS.map(o => o.value), DEFAULT_SORT);
  const [field, dir] = value.split(':') as [SortField, SortDir];
  return { field, dir };
};

const formatBanDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

// ─── sub-components ──────────────────────────────────────────────────────────

type PopoverPos = { top: number; left: number };

const POPOVER_WIDTH = 260;

// Поповер position:fixed — карточка таблицы с overflow:hidden его бы обрезала.
const BanStatus: React.FC<{ player: Player }> = ({ player }) => {
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = `plp-ban-${player.uid}`;
  const summary = player.banSummary ?? null;
  const banned = Boolean(player.banned);

  const close = useCallback(() => setPos(null), []);

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8));
    const below = window.innerHeight - rect.bottom > 160;
    setPos({ left, top: below ? rect.bottom + 6 : Math.max(8, rect.top - 6 - 130) });
  };

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos, close]);

  if (!banned && !summary) return <span className="plp-status-pill active">{tr.players.status.active}</span>;

  const trigger = banned ? (
    <button
      type="button"
      ref={triggerRef}
      className="plp-status-pill banned plp-ban-trigger"
      aria-expanded={pos !== null}
      aria-controls={popoverId}
      onClick={e => { e.stopPropagation(); if (pos) close(); else open(); }}
    >
      {tr.players.list.banTrigger}
    </button>
  ) : (
    <>
      <span className="plp-status-pill active">{tr.players.status.active}</span>
      <button
        type="button"
        ref={triggerRef}
        className="plp-ban-mark"
        aria-expanded={pos !== null}
        aria-controls={popoverId}
        onClick={e => { e.stopPropagation(); if (pos) close(); else open(); }}
      >
        {tr.players.list.wasBanned(summary?.count ?? 0)}
      </button>
    </>
  );

  return (
    <span className="plp-ban-status">
      {trigger}
      {pos && (
        <div
          id={popoverId}
          role="dialog"
          className="plp-ban-popover"
          style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
          onClick={e => e.stopPropagation()}
        >
          {banned && (
            <p className="plp-ban-line">
              <b>{tr.players.list.banNow}</b> {player.bannedUntil ? tr.players.until(formatBanDate(player.bannedUntil)) : tr.players.forever}
              {player.banReason ? ` · ${tr.players.reasonQuoted(player.banReason)}` : ''}
            </p>
          )}
          {summary && (
            <p className="plp-ban-line">
              {tr.players.list.banSummary(summary.count, formatBanDate(summary.lastBannedAt))}
              {summary.lastReason ? ` · ${tr.players.reasonQuoted(summary.lastReason)}` : ''}
            </p>
          )}
        </div>
      )}
    </span>
  );
};

type MenuState = { uid: string } | null;

const DotsMenu: React.FC<{
  uid: string;
  open: boolean;
  onToggle: () => void;
  onProfile: () => void;
}> = ({ uid, open, onToggle, onProfile }) => (
  <div style={{ position: 'relative' }}>
    <button
      type="button"
      className="plp-menu-btn"
      onClick={e => { e.stopPropagation(); onToggle(); }}
      aria-label={tr.players.list.actionsAria}
    >
      ···
    </button>
    {open && (
      <div className="plp-menu-dropdown">
        <button
          type="button"
          className="plp-menu-item"
          onClick={e => { e.stopPropagation(); onProfile(); }}
        >
          {tr.players.list.openProfile}
        </button>
      </div>
    )}
  </div>
);

// ─── Top-4 card ───────────────────────────────────────────────────────────────

const TopPlayerCard: React.FC<{
  player: Player;
  rank: number;
  tags: Map<string, Tag>;
  matchType: string;
  onClick: () => void;
}> = ({ player, rank, tags, matchType, onClick }) => {
  const rating = modeRating(player, matchType);
  const matches = modeMatches(player, matchType);
  const tag = player.tag ? tags.get(player.tag) : null;

  return (
    <div
      className="plp-top-card hero-surface"
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      tabIndex={0}
      role="button"
      aria-label={tr.players.list.profileAria(player.name || player.uid)}
    >
      <span className={`plp-top-card-rank${rank === 1 ? ' rank-1' : ''}`}>#{rank}</span>
      <PlayerAvatar
        uid={player.uid}
        displayName={player.name || player.steamProfileName}
        avatarUrl={player.avatarUrl}
        size={56}
        glowRing
      />
      <p className="plp-top-card-name">{player.name || player.steamProfileName || player.uid}</p>
      <span className="plp-top-card-uid">{player.uid}</span>
      <span className={`plp-top-card-rating${rank === 1 ? ' rank-1' : ''}`}>{fmtRating(rating)}</span>
      <span className="plp-top-card-matches">{tr.players.list.matchesCount(matches)}</span>
      {tag ? (
        <span
          className="plp-top-card-tag"
          style={tag.colorHex ? { background: tag.colorHex, color: '#fff', borderColor: 'transparent' } : undefined}
        >
          {tag.name || tag.code}
        </span>
      ) : (
        <span className="plp-top-card-tag" style={{ opacity: 0.5 }}>{tr.players.list.noTag}</span>
      )}
    </div>
  );
};

// ─── main ────────────────────────────────────────────────────────────────────

const emptyPage: PlayersPage = {
  content: [],
  page: 0,
  size: 10,
  totalPages: 0,
  totalElements: 0,
  remainingPages: 0
};

const PlayersPageView: React.FC<PlayersPageProps> = ({ selfUid, viewAsAdmin = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── state ──────────────────────────────────────────────────────
  const [playersPage, setPlayersPage] = useState<PlayersPage>(emptyPage);
  const [topPlayers, setTopPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [topLoading, setTopLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FiltersState>(() => readFiltersFromUrl(searchParams, viewAsAdmin));
  const [searchInput, setSearchInput] = useState(filters.search);
  const [sortField, setSortField] = useState<SortField>(() => readSortFromUrl(searchParams).field);
  const [sortDir, setSortDir] = useState<SortDir>(() => readSortFromUrl(searchParams).dir);
  const [menuOpen, setMenuOpen] = useState<MenuState>(null);
  const requestSeqRef = useRef(0);

  const filtersRef = useRef(filters);
  const sortFieldRef = useRef(sortField);
  const sortDirRef = useRef(sortDir);

  const { pushToast } = useAppToast();

  const [tags, setTags] = useState<Tag[]>([]);
  const tagsByCode = React.useMemo(
    () => new Map(tags.map(t => [t.code, t] as const)),
    [tags]
  );

  // keep refs in sync
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortFieldRef.current = sortField; }, [sortField]);
  useEffect(() => { sortDirRef.current = sortDir; }, [sortDir]);

  // ── fetch top-4 ─────────────────────────────────────────────────
  const fetchTop4 = useCallback(async () => {
    try {
      setTopLoading(true);
      const res = await api.post<PlayersPage>('/api/players/search', {
        page: 0,
        size: 4,
        sorts: [{ field: 'rating', direction: 'desc', matchTypeCode: filtersRef.current.matchType }]
      });
      setTopPlayers((res.data?.content || []).slice(0, 4));
    } catch {
      // top-4 failure is non-critical
    } finally {
      setTopLoading(false);
    }
  }, []);

  // ── fetch players ────────────────────────────────────────────────
  const fetchPlayers = useCallback(async (page = 0) => {
    const seq = ++requestSeqRef.current;
    try {
      setLoading(true);
      setError(null);

      const f = filtersRef.current;
      const sf = sortFieldRef.current;
      const sd = sortDirRef.current;

      // Search: try uid first if numeric-ish, else name
      const trimmed = f.search.trim();
      const uidQuery = /^\d+$/.test(trimmed) ? trimmed : null;
      const nameQuery = trimmed && !uidQuery ? trimmed : null;

      const payload: Record<string, unknown> = {
        page,
        size: playersPage.size,
        sorts: [{ field: sf, direction: sd, matchTypeCode: f.matchType }]
      };

      if (uidQuery) payload.uid = uidQuery;
      if (nameQuery) payload.name = nameQuery;
      if (f.banned !== '') payload.banned = f.banned === 'true';
      if (viewAsAdmin) payload.everBanned = f.everBanned === '' ? null : f.everBanned === 'true';

      const res = await api.post<PlayersPage>('/api/players/search', payload);
      if (seq !== requestSeqRef.current) return;
      setPlayersPage(res.data || emptyPage);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      const msg = getErrorMessage(err, tr.players.list.loadFailed);
      setError(msg);
      pushToast(msg);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [playersPage.size, pushToast, viewAsAdmin]);

  // ── init ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchTop4();
    fetchPlayers(0);
    api.get<Tag[]>('/api/tags')
      .then(res => setTags(res.data || []))
      .catch(() => { /* non-critical, falls back to raw code */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── close menu on outside click ─────────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const handler = () => setMenuOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpen]);

  // ── handlers ────────────────────────────────────────────────────
  // Фильтры и сортировка живут в URL: ссылкой можно поделиться, F5 их не сбрасывает.
  const writeStateToUrl = (f: FiltersState, field: SortField, dir: SortDir) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const set = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      set('q', f.search.trim());
      set('banned', f.banned);
      set('everBanned', viewAsAdmin ? f.everBanned : '');
      set('mode', f.matchType === defaultFilters.matchType ? '' : f.matchType);
      const sort = `${field}:${dir}`;
      set('sort', sort === DEFAULT_SORT ? '' : sort);
      return params;
    }, { replace: true });
  };

  const applySortState = (field: SortField, dir: SortDir) => {
    sortFieldRef.current = field;
    sortDirRef.current = dir;
    setSortField(field);
    setSortDir(dir);
    writeStateToUrl(filtersRef.current, field, dir);
    fetchPlayers(0);
  };

  const applySort = (field: SortField) => {
    const nextDir = sortFieldRef.current === field && sortDirRef.current === 'desc' ? 'asc' : 'desc';
    applySortState(field, nextDir);
  };

  const applyFilter = (patch: Partial<FiltersState>) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    writeStateToUrl(next, sortFieldRef.current, sortDirRef.current);
    fetchPlayers(0);
  };

  // Живой поиск: применяем через паузу после ввода, Enter — сразу.
  useEffect(() => {
    if (searchInput === filtersRef.current.search) return;
    const timer = window.setTimeout(() => {
      if (filtersRef.current.search !== searchInput) applyFilter({ search: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const hasActiveFilters =
    filters.search.trim() !== '' ||
    filters.banned !== '' ||
    filters.everBanned !== '' ||
    filters.matchType !== defaultFilters.matchType ||
    `${sortField}:${sortDir}` !== DEFAULT_SORT;

  const resetFilters = () => {
    filtersRef.current = defaultFilters;
    sortFieldRef.current = 'rating';
    sortDirRef.current = 'desc';
    setFilters(defaultFilters);
    setSearchInput('');
    setSortField('rating');
    setSortDir('desc');
    writeStateToUrl(defaultFilters, 'rating', 'desc');
    fetchPlayers(0);
    fetchTop4();
  };

  const openPlayer = (uid: string) => {
    const player = playersPage.content.find(p => p.uid === uid);
    navigate(`/players/${encodeURIComponent(uid)}`, { state: { player } });
  };

  const { page, totalPages, totalElements } = playersPage;
  const content = playersPage.content || [];
  const showRank = sortField === 'rating' && sortDir === 'desc';
  const columnCount = showRank ? 10 : 9;

  const sortLabel = (field: SortField, label: string) => {
    const active = sortField === field;
    const arrow = active ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';
    return label + arrow;
  };

  return (
    <div className="plp-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="plp-header">
        <h1 className="plp-title">{tr.players.list.title}</h1>
      </div>

      {/* ── Filters ─────────────────────────────────────────── */}
      <div className="plp-filter-card">
        <input
          type="search"
          className="plp-search-input"
          placeholder={tr.players.list.searchPlaceholder}
          aria-label={tr.players.list.searchAria}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyFilter({ search: searchInput }); }}
        />

        <select
          className="plp-select"
          aria-label={tr.players.list.statusAria}
          value={filters.banned}
          onChange={e => applyFilter({ banned: e.target.value as FiltersState['banned'] })}
        >
          <option value="">{tr.players.list.statusAll}</option>
          <option value="false">{tr.players.status.active}</option>
          <option value="true">{tr.players.status.banned}</option>
        </select>

        {viewAsAdmin && (
          <select
            className="plp-select"
            aria-label={tr.players.list.everBannedAria}
            value={filters.everBanned}
            onChange={e => applyFilter({ everBanned: e.target.value as FiltersState['everBanned'] })}
          >
            <option value="">{tr.players.list.everBannedAll}</option>
            <option value="true">{tr.players.list.everBanned}</option>
            <option value="false">{tr.players.list.neverBanned}</option>
          </select>
        )}

        <select
          className="plp-select"
          aria-label={tr.players.list.modeAria}
          value={filters.matchType}
          onChange={e => {
            applyFilter({ matchType: e.target.value });
            fetchTop4();
          }}
          title={tr.players.list.modeTitle}
        >
          {MATCH_MODES.map(m => (
            <option key={m.code} value={m.code}>{tr.players.list.mode(m.label)}</option>
          ))}
        </select>

        <div className="plp-filter-gap" />

        {hasActiveFilters && (
          <button type="button" className="plp-reset-btn" onClick={resetFilters}>
            {tr.players.list.resetFilters}
          </button>
        )}

        <select
          className="plp-select"
          aria-label={tr.players.list.sortAria}
          value={`${sortField}:${sortDir}`}
          onChange={e => {
            const [field, dir] = e.target.value.split(':') as [SortField, SortDir];
            applySortState(field, dir);
          }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* ── Top-4 ───────────────────────────────────────────── */}
      {(topLoading || topPlayers.length > 0) && (
        <div className="plp-top-section">
          <div className="plp-top-header">
            <h2 className="plp-top-title">{tr.players.list.topTitle}</h2>
            <Link
              to="/players"
              className="plp-top-link"
              onClick={resetFilters}
            >
              {tr.players.list.fullTop}
            </Link>
          </div>
          <div className="plp-top-grid">
            {topLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="plp-top-card"
                    style={{ minHeight: 160, opacity: 0.4 }}
                  />
                ))
              : topPlayers.map((player, i) => (
                  <TopPlayerCard
                    key={player.uid}
                    player={player}
                    rank={i + 1}
                    tags={tagsByCode}
                    matchType={filters.matchType}
                    onClick={() => openPlayer(player.uid)}
                  />
                ))}
          </div>
        </div>
      )}

      {/* ── Found info ──────────────────────────────────────── */}
      {!loading && !error && (
        <div className="plp-found-row">
          <span className="plp-found-count">{tr.players.list.found(totalElements)}</span>
          {totalPages > 1 && (
            <span className="plp-found-page">
              {tr.players.list.pageOf(page + 1, totalPages)}
            </span>
          )}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────── */}
      <div className="plp-table-card">
        <table className="plp-table">
          <thead>
            <tr>
              {showRank && <th className="plp-th-rank">#</th>}
              <th>{tr.players.list.columns.player}</th>
              <th>{tr.players.list.columns.tags}</th>
              <th>{tr.players.list.columns.status}</th>
              <th>{tr.players.list.columns.subscription}</th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => applySort('rating')}
                title={tr.players.list.sortByRating}
              >
                {sortLabel('rating', tr.players.list.columns.rating)}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => applySort('matchesCount')}
                title={tr.players.list.sortByMatches}
              >
                {sortLabel('matchesCount', tr.players.list.columns.matches)}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => applySort('balance')}
                title={tr.players.list.sortByBalance}
              >
                {sortLabel('balance', tr.players.list.columns.balance)}
              </th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && content.length === 0 ? (
              <tr className="plp-state-row">
                <td colSpan={columnCount} style={{ padding: 0 }}>
                  <TableSkeleton
                    withAvatar
                    rows={10}
                    columns={showRank
                      ? ['4%', '22%', '11%', '10%', '10%', '10%', '9%', '8%', '6%', '6%']
                      : ['24%', '12%', '11%', '11%', '11%', '10%', '9%', '6%', '6%']}
                    showHeader={false}
                    ariaLabel={tr.players.list.loadingAria}
                  />
                </td>
              </tr>
            ) : error ? (
              <tr className="plp-state-row">
                <td colSpan={columnCount}>
                  <ErrorState title={tr.players.list.errorTitle} description={error} />
                </td>
              </tr>
            ) : content.length === 0 ? (
              <tr className="plp-state-row">
                <td colSpan={columnCount}>
                  <EmptyState
                    title={tr.players.list.emptyTitle}
                    description={hasActiveFilters ? tr.players.list.emptyDescription : undefined}
                    actions={hasActiveFilters ? [{ label: tr.players.list.resetFilters, onClick: resetFilters, variant: 'outline' }] : undefined}
                  />
                </td>
              </tr>
            ) : (
              content.map((player, index) => {
                const isSelf = Boolean(selfUid && player.uid === selfUid);
                const tag = player.tag ? tagsByCode.get(player.tag) : null;
                const rating = modeRating(player, filters.matchType);
                const matches = modeMatches(player, filters.matchType);
                const isMenuOpen = menuOpen?.uid === player.uid;

                return (
                  <tr
                    key={player.uid}
                    onClick={() => openPlayer(player.uid)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openPlayer(player.uid);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={tr.players.list.openProfileAria(player.name || player.uid)}
                  >
                    {showRank && (
                      <td className="plp-td-rank" data-label={tr.players.list.columns.rank}>
                        {page * playersPage.size + index + 1}
                      </td>
                    )}

                    {/* ИГРОК */}
                    <td className="plp-td-player">
                      <div className="plp-cell-player">
                        <PlayerAvatar
                          uid={player.uid}
                          displayName={player.name || player.steamProfileName}
                          avatarUrl={player.avatarUrl}
                          size={36}
                        />
                        <div className="plp-cell-player-info">
                          <span className="plp-cell-player-name">
                            {player.name || player.steamProfileName || player.uid}
                            {isSelf && <span className="plp-self-pill">{tr.players.list.you}</span>}
                          </span>
                          <span className="plp-cell-player-uid">{player.uid}</span>
                        </div>
                      </div>
                    </td>

                    {/* ТЕГИ */}
                    <td data-label={tr.players.list.columns.tags}>
                      <div className="plp-cell-tags">
                        {tag ? (
                          <span
                            className="plp-tag-pill"
                            style={
                              tag.colorHex
                                ? { background: tag.colorHex, color: '#fff', borderColor: 'transparent' }
                                : undefined
                            }
                          >
                            {tag.name || tag.code}
                          </span>
                        ) : (
                          <span className="plp-tag-pill" style={{ opacity: 0.4 }}>—</span>
                        )}
                      </div>
                    </td>

                    {/* СТАТУС */}
                    <td data-label={tr.players.list.columns.status}>
                      <BanStatus player={player} />
                    </td>

                    {/* ПОДПИСКА */}
                    <td data-label={tr.players.list.columns.subscription}>
                      <span className={`plp-status-pill ${player.subscriptionActive ? 'sub' : 'muted'}`}>
                        {player.subscriptionActive ? tr.players.list.subscriptionActive : tr.players.list.subscriptionNone}
                      </span>
                    </td>

                    {/* РЕЙТИНГ */}
                    <td data-label={tr.players.list.columns.rating}>
                      <span className="plp-cell-mono">{fmtRating(rating)}</span>
                    </td>

                    {/* МАТЧЕЙ */}
                    <td data-label={tr.players.list.columns.matches}>
                      <span className="plp-cell-mono">{matches}</span>
                    </td>

                    {/* БАЛАНС */}
                    <td data-label={tr.players.list.columns.balance}>
                      <span className="plp-cell-mono">{fmtRating(player.balance ?? 0)}</span>
                    </td>

                    {/* ПРОФИЛЬ */}
                    <td className="plp-td-action" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className="plp-profile-btn"
                        onClick={() => openPlayer(player.uid)}
                      >
                        {tr.players.list.profile}
                      </button>
                    </td>

                    {/* ··· MENU */}
                    <td className="plp-td-menu" onClick={e => e.stopPropagation()}>
                      <DotsMenu
                        uid={player.uid}
                        open={isMenuOpen}
                        onToggle={() =>
                          setMenuOpen(isMenuOpen ? null : { uid: player.uid })
                        }
                        onProfile={() => {
                          setMenuOpen(null);
                          openPlayer(player.uid);
                        }}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ──────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="plp-pagination">
          <span className="plp-pagination-info">
            {tr.players.pagination.info(page + 1, totalPages, totalElements)}
          </span>
          <div className="plp-pagination-btns">
            <button
              type="button"
              className="plp-page-btn"
              disabled={page === 0}
              onClick={() => fetchPlayers(page - 1)}
            >
              {tr.players.pagination.back}
            </button>
            <button
              type="button"
              className="plp-page-btn current"
              disabled
            >
              {page + 1}
            </button>
            <button
              type="button"
              className="plp-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => fetchPlayers(page + 1)}
            >
              {tr.players.pagination.next}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayersPageView;
